import type { InferType } from 'groqd'
import { q } from 'groqd'

export const LEARN_INDEX_PAGE_SIZE = 15
export const LEARN_FEATURED_COUNT = 3

// Only what the index page renders (description feeds the search filter) —
// keep the projection lean, it's shipped in the SSR payload and re-fetched
// client-side for search
const learnPostSchema = q.object({
  _id: q.string(),
  title: q.string(),
  author: q.string(),
  slug: q.object({
    current: q.string(),
  }),
  description: q.string().nullable(),
  date: q.string(),
})

const postProjection = `{
    _id,
    title,
    "author": author->name,
    slug { current },
    "description": openGraphDescription,
    "date": coalesce(date + "T00:00:00Z", _createdAt)
  }`

// Fetched as three flat lists and combined in JS: filtering standalone posts
// with `!(_id in *[...])` directly in GROQ re-runs the metadata subquery per
// post, which made this query ~4x slower than the equivalent set difference here
export const learnPostsQuery = `{
  "translatedPosts": *[
    _type == "translation.metadata" &&
    "learnPosts" in schemaTypes &&
    !(_id in path("drafts.**"))
  ]{
    "post": coalesce(
      translations[_key == $language][0].value->${postProjection},
      translations[_key == "en-US"][0].value->${postProjection}
    )
  }[].post,
  "referencedIds": *[
    _type == "translation.metadata" &&
    "learnPosts" in schemaTypes &&
    !(_id in path("drafts.**"))
  ].translations[].value._ref,
  "standalonePosts": *[
    _type == "learnPosts" &&
    language == "en-US" &&
    !(_id in path("drafts.**"))
  ]${postProjection}
}`

const learnPostsResponseSchema = q.object({
  translatedPosts: q.array(learnPostSchema.nullable()),
  referencedIds: q.array(q.string().nullable()),
  standalonePosts: q.array(learnPostSchema),
})

export type LearnPost = InferType<typeof learnPostSchema>
export type LearnPosts = LearnPost[]

export type LearnSanityConfig = {
  projectId: string
  dataset: string
  apiVersion: string
}

// Duplicate translation.metadata documents in Sanity would make the same
// post appear twice, so keep only the first occurrence of each _id
function dedupeById(posts: LearnPosts) {
  const seenIds = new Set<string>()
  return posts.filter((post) => {
    if (seenIds.has(post._id)) return false
    seenIds.add(post._id)
    return true
  })
}

function notNull<T>(value: T | null): value is T {
  return value !== null
}

export function parseLearnPostsResponse(response: unknown): LearnPosts {
  const { translatedPosts, referencedIds, standalonePosts } =
    learnPostsResponseSchema.parse(response, {
      errorMap: (issue, _ctx) => {
        const [listKey, index, attribute] = issue.path
        const post = (response as Record<string, { title?: string }[]>)?.[
          listKey
        ]?.[index as number]
        return {
          message: `Error in post "${post?.title}" with the attribute "${attribute}"`,
          data: _ctx.data,
        }
      },
    })
  const referenced = new Set(referencedIds.filter(notNull))
  const posts = [
    ...translatedPosts.filter(notNull),
    ...standalonePosts.filter((post) => !referenced.has(post._id)),
  ]
  posts.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  return dedupeById(posts)
}

export async function fetchAllLearnPosts(
  sanity: LearnSanityConfig,
  language: string
): Promise<LearnPosts> {
  const url = new URL(
    `https://${sanity.projectId}.apicdn.sanity.io/v${sanity.apiVersion}/data/query/${sanity.dataset}`
  )
  url.searchParams.set('query', learnPostsQuery)
  url.searchParams.set('$language', JSON.stringify(language))
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Learn posts query failed with status ${response.status}`)
  }
  const { result } = await response.json()
  return parseLearnPostsResponse(result)
}
